import { OpenInNew, Search } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { formatDate } from '../../helpers/formatters';
import { openMercadoPagoSubscription } from '../../helpers/mercadoPagoLinks';

const STATUS_FILTERS = [
    { value: '', label: 'Todos' },
    { value: 'authorized', label: 'Authorized' },
    { value: 'pending', label: 'Pending' },
    { value: 'paused', label: 'Paused' },
    { value: 'cancelled', label: 'Cancelled' },
];

function statusChip(row) {
    const status = String(row?.status || '').toLowerCase();
    if (row?.grace_until) {
        return { label: 'Grace', color: 'warning' };
    }
    if (row?.trial_active && status === 'authorized') {
        return { label: 'Trial', color: 'info' };
    }
    if (status === 'authorized') {
        return { label: 'Authorized', color: 'success' };
    }
    if (status === 'pending') {
        return { label: 'Pending', color: 'warning' };
    }
    if (status === 'paused') {
        return { label: 'Paused', color: 'default' };
    }
    if (status === 'cancelled') {
        return { label: 'Cancelled', color: 'default' };
    }
    return { label: status || '—', color: 'default' };
}

function MpSubscriptionsListPage() {
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const loadRows = useCallback(async () => {
        if (!isMaster) {
            return;
        }
        setLoading(true);
        try {
            const response = await api.get('/mp-subscriptions', {
                params: {
                    q: appliedSearch || undefined,
                    status: statusFilter || undefined,
                },
            });
            setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
        } catch (error) {
            setRows([]);
            await Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: error?.response?.data?.message
                    || error?.response?.data?.error
                    || 'Não foi possível carregar as assinaturas MP.',
            });
        } finally {
            setLoading(false);
        }
    }, [isMaster, appliedSearch, statusFilter]);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (cancelled || !user || typeof user.is_master !== 'boolean') {
                    return;
                }
                patchStoredUser({ is_master: user.is_master });
                setIsMaster(user.is_master);
            })
            .catch(() => setIsMaster(false))
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!checkingAccess && isMaster) {
            loadRows();
        }
    }, [checkingAccess, isMaster, loadRows]);

    if (checkingAccess) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
                    Dashboard
                </Link>
                <Typography color="text.primary">Assinaturas MP</Typography>
            </Breadcrumbs>

            <Typography variant="h5" sx={{ mb: 1 }}>Assinaturas Mercado Pago</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Espelho local das assinaturas. Disputas, cartão e estornos ficam no painel do Mercado Pago.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Buscar nome, e-mail, plano ou preapproval"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setAppliedSearch(searchTerm.trim());
                        }
                    }}
                    sx={{ minWidth: { xs: '100%', sm: 320 } }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {STATUS_FILTERS.map((option) => (
                            <MenuItem key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" startIcon={<Search />} onClick={() => setAppliedSearch(searchTerm.trim())}>
                    Buscar
                </Button>
                <IconButton
                    aria-label="Limpar filtros"
                    onClick={() => {
                        setSearchTerm('');
                        setAppliedSearch('');
                        setStatusFilter('');
                    }}
                >
                    <ClearIcon />
                </IconButton>
            </Stack>

            {loading ? (
                <Loading />
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Usuário</TableCell>
                                <TableCell>Plano</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Próx. cobrança</TableCell>
                                <TableCell>Último pagamento</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhuma assinatura MP encontrada.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => {
                                    const chip = statusChip(row);
                                    return (
                                        <TableRow key={row.uuid} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {row.user_name || '—'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {row.user_email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{row.plan_name || '—'}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={chip.label} color={chip.color} />
                                                {row.grace_until ? (
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                        Grace até {formatDate(row.grace_until)}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>{row.next_payment_date ? formatDate(row.next_payment_date) : '—'}</TableCell>
                                            <TableCell>{row.last_payment_status || '—'}</TableCell>
                                            <TableCell align="right">
                                                {row.mp_preapproval_id ? (
                                                    <Tooltip title="Abrir no Mercado Pago">
                                                        <IconButton onClick={() => openMercadoPagoSubscription(row.mp_preapproval_id)}>
                                                            <OpenInNew fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                                <Tooltip title="Editar usuário">
                                                    <Button
                                                        size="small"
                                                        onClick={() => navigate(`/admin/edit-user/${row.user_uuid}`)}
                                                    >
                                                        Usuário
                                                    </Button>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default MpSubscriptionsListPage;
